import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Group, Stack, Anchor, ActionIcon, Drawer, Burger } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconChevronDown, IconHome, IconExternalLink } from '@tabler/icons-react';
import { Link, useLocation } from 'react-router-dom';
import { THEME_COLORS } from '../../theme/ThemeConstants';
import { CartIconButton } from '../cart/CartIconButton';
import './TopNavigation.scss';

interface NavLink {
  label: string;
  path: string;
  icon?: React.ReactNode;
}

interface TopNavigationProps {
  transparent?: boolean;
}

const navLinks: NavLink[] = [{ label: 'Store', path: '/', icon: <IconHome size={16} /> }];

export const TopNavigation: React.FC<TopNavigationProps> = observer(({ transparent = false }) => {
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' && !location.hash;
    }
    return location.pathname === path;
  };

  const containerStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: transparent ? 'rgba(15, 15, 15, 0.4)' : THEME_COLORS.background.secondary,
    backdropFilter: transparent ? 'blur(24px)' : undefined,
    WebkitBackdropFilter: transparent ? 'blur(24px)' : undefined,
    borderBottom: `1px solid ${transparent ? 'rgba(255,255,255,0.08)' : THEME_COLORS.border.primary}`
  };

  const innerStyle: React.CSSProperties = {
    width: '100%',
    padding: '0 24px'
  };

  return (
    <>
      <Box style={containerStyle}>
        <Box style={innerStyle}>
          {isMobile ? (
            <Group justify='flex-end' gap={8} h={56} style={{ width: '100%' }}>
              <CartIconButton />
              <Burger opened={menuOpen} onClick={() => setMenuOpen(!menuOpen)} color='white' size='sm' />
            </Group>
          ) : (
            <Group justify='space-between' h={56} style={{ width: '100%' }}>
              <Group gap={28}>
                {navLinks.map((link) => (
                  <Anchor
                    key={link.path}
                    component={Link}
                    to={link.path}
                    underline='never'
                    className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    {link.icon}
                    {link.label}
                  </Anchor>
                ))}
              </Group>
              <Group gap={24}>
                <Anchor
                  href='https://trappedmc.com'
                  target='_blank'
                  rel='noopener noreferrer'
                  underline='never'
                  className='nav-link'
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  Main Site
                  <IconExternalLink size={16} />
                </Anchor>
                <CartIconButton size={20} />
              </Group>
            </Group>
          )}
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        opened={menuOpen}
        onClose={() => setMenuOpen(false)}
        size='75%'
        position='right'
        withCloseButton={false}
        classNames={{
          content: 'mobile-drawer-content',
          body: 'mobile-drawer-body'
        }}
      >
        <Stack gap='xs' className='mobile-nav-stack'>
          <Box className='mobile-nav-header'>
            <ActionIcon variant='subtle' onClick={() => setMenuOpen(false)} className='mobile-close'>
              <IconChevronDown size={20} style={{ transform: 'rotate(-90deg)' }} />
            </ActionIcon>
          </Box>

          <Box className='mobile-nav-section'>
            {navLinks.map((link) => (
              <Anchor
                key={link.path}
                component={Link}
                to={link.path}
                underline='never'
                className={`mobile-nav-link ${isActive(link.path) ? 'active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {link.icon}
                {link.label}
              </Anchor>
            ))}
            <Anchor
              href='https://trappedmc.com'
              target='_blank'
              rel='noopener noreferrer'
              underline='never'
              className='mobile-nav-link'
              onClick={() => setMenuOpen(false)}
            >
              <IconExternalLink size={18} />
              Main Site
            </Anchor>
          </Box>
        </Stack>
      </Drawer>
    </>
  );
});
